import { Dialog, DialogTitle, DialogContent, Box, Typography, DialogActions, Button } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { apiConstants, patch } from '../../common/constants';
import { getMerchant } from '../../helpers/ReduxHelpers';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE, BORDER, PRIMARY_BLUE } from '../../styles/foundation/colors';

const Confirm = ({
    openDialog,
    setOpenDialog,
    isActive,
    title,
    hasConfigValue,
    getDefaultPrice,
    handleConfirm,
    configType,
    selectedConfig,
}) => {


    const merchant = getMerchant();
    const merchantId = merchant.id ?? '';

    // The charge the merchant will actually pay: their configured rate when one is set, else the
    // platform default. getDefaultPrice() alone is wrong once a rate is configured — it showed the
    // default (e.g. ₹0.6) under the words "as configured" while the card showed the real ₹5.
    // Callers that do not pass selectedConfig fall back to the default, as before.
    const chargeToShow = (hasConfigValue() && selectedConfig?.configValue)
        ? selectedConfig.configValue
        : getDefaultPrice();

    const handleConfirmWithConfig = async () => {
        handleConfirm();

        if (!isActive && !hasConfigValue()) {
            let payload = {};
            const defaultPrice = getDefaultPrice();

            switch (configType) {
                case 'NDR_WHATSAPP':
                    payload = {
                        perAWBNdr: defaultPrice
                    };
                    break;

                case 'NDR_CALLING':
                    payload = {
                        perAWBCall: defaultPrice
                    };
                    break;

                case 'channel_orders_confirmation_WA':
                    payload = {
                        perChannelOrderConfirmationWA: defaultPrice
                    };
                    break;

                case 'channel_orders_address_verification':
                    payload = {
                        perChannelAddressVerificationWA: defaultPrice
                    };
                    break;

                case 'SMS':
                    payload = {
                        perSMS: defaultPrice
                    };
                    break;

                case 'WA':
                    payload = {
                        perWA: defaultPrice
                    };
                    break;

                // Email has no per-message rate in communicationPricing, so there is nothing to seed.
                case 'EMAIL':
                    payload = {};
                    break;
                default:
                    console.warn(`Unhandled config type: ${configType}`);
                    payload = {};
                    break;
            }

            if (Object.keys(payload).length > 0) {
                await addValToConfig(merchantId, payload);
            }
        }
        setOpenDialog(false);
    };

    return (
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY }}>
                {isActive ? "Disable" : "Enable"} {title}?
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, color: SUBTLE, mb: 2 }}>
                    {isActive
                        ? `This will stop ${title} and you will no longer be charged for it.`
                        : `This will turn on ${title} for your orders.`}
                </Typography>
                {/* Price-forward framing: state the charge explicitly before enabling.
                    Only channels that actually carry a rate get this box — email has none, so it
                    would otherwise claim a ₹0 charge. */}
                {!isActive && (hasConfigValue() || selectedConfig?.defaultPriceValue) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        border: `1px solid ${BORDER}`,
                        borderRadius: '8px',
                        bgcolor: '#f4f8ff',
                        p: 1.25,
                    }}>
                        <ReceiptLongIcon sx={{ fontSize: 18, color: PRIMARY_BLUE, mt: '1px' }} />
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.5 }}>
                            After you confirm, you will be charged{' '}
                            <Typography component="span" sx={{ fontWeight: WEIGHT.bold, color: NAVY }}>
                                ₹{chargeToShow}
                            </Typography>{' '}
                            {hasConfigValue() ? 'as configured.' : 'at the default price.'}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={() => setOpenDialog(false)} sx={{ fontFamily: FONT, fontSize: SIZE.xs, textTransform: 'none', color: SUBTLE }}>Cancel</Button>
                <Button onClick={handleConfirmWithConfig} variant="contained" sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, textTransform: 'none' }}>
                    {isActive ? 'Confirm and disable' : 'Confirm and enable'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Confirm;

export const addValToConfig = async (merchantId, payload) => {
    try {
        const res = await patch(`${apiConstants.BASE_LOCAL}${apiConstants.COMMUNICATION_CONFIG}/${merchantId}/pricing/communication/update`, payload);
        if (res.status === 200) {
            // enqueueSnackbar('Success', { variant: 'success' });
        } else {
            // enqueueSnackbar('Some error occurred', { variant: 'error' });
        }
    } catch (err) {
        console.error(err);
        // enqueueSnackbar('Failed to submit. Please try again later.', { variant: 'error' });
    }
};
